<script>
  import { ComponentType } from '../../../../../../../../js/constants';
  import { getNavState } from '../../../../../../../../js/stores';
  import { hoverWithDelay } from '../../../../../../../../js/hoverWithDelay';
  import { onClickOutside } from '../../../../../../../../js/onClickOutside';
  import { onTouchOrClick } from '../../../../../../../../js/onTouchOrClick';
  import { getCategory, getSubcategory, getNavDeviceType } from '../../../../../../../../js/trackingUtils';
  import { trackCdp } from '../../../../../../../../js/tracking';

  import SubcategoryList from './SubcategoryList/SubcategoryList.svelte';

  // Props
  export let id;
  export let title;
  export let description;
  export let navItems = [];

  // Constants
  const TIMEOUT_ENTER_DURATION = 0;
  const TIMEOUT_LEAVE_DURATION = 300;
  const navStateStore = getNavState();
  const trackData = { menuId: id, menuText: title };

  // Variables
  let element;
  let isOpen = false;

  // Reactive declarations
  $: isCurrentSubcategory = $navStateStore.subcategory === element;

  // Open the subcategory
  function open() {
    if (isOpen) {
      return;
    }
    isOpen = true;
    $navStateStore = {
      ...$navStateStore,
      subcategory: element, // Set current subcategory
    };
    trackCdp({
      eventName: 'Open Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.MAIN_NAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  // Close the subcategory
  function close() {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    if (isCurrentSubcategory) {
      $navStateStore = {
        ...$navStateStore,
        subcategory: null, // Clear current subcategory
      };
    }
    trackCdp({
      eventName: 'Close Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.MAIN_NAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  // Toggle the subcategory
  function toggle() {
    isOpen ? close() : open();
  }

  // Ensure only one subcategory is open at a time.
  $: if (isOpen && !isCurrentSubcategory) {
    close();
  }
</script>

<div
  {id}
  class="Subcategory"
  class:isOpen
  data-subcategory={title}
  bind:this={element}
  use:hoverWithDelay={[open, close, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION]}
  use:onClickOutside={close}
>
  <button
    class="toggle nav-button"
    class:isOpen
    class:noDescriptions={!description}
    aria-haspopup="true"
    aria-expanded={isOpen ? true : false}
    use:onTouchOrClick={toggle}
  >
    <span class="title">{title}</span>
    {#if description}<span class="description">{description}</span>{/if}
  </button>
  <SubcategoryList {isOpen} {navItems} hasDescriptions={Boolean(description)} />
</div>

<style>@charset "UTF-8";
.Subcategory {
  margin: 0;
  padding: 0;
}

.toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  display: block;
  overflow: hidden;
  position: relative;
  min-height: 0;
  max-height: none;
  max-width: 278px;
  width: 100%;
  margin: 0;
  padding: 10px 20px;
  border-radius: 0;
  border: 0;
  outline: 0;
  color: var(--color-text, #1a1a1a);
  background-color: transparent;
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: 400;
  letter-spacing: normal;
  text-align: left;
  text-decoration: none;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.toggle.nav-button::-moz-focus-inner {
  border: 0;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.toggle.nav-button:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button.isOpen::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button > * {
  position: relative;
}

.toggle.nav-button.noDescriptions {
  max-width: 238px;
}

.title {
  display: block;
  position: relative;
  font-size: 16px;
  line-height: 28px;
  font-weight: var(--font-weight-medium, 500);
}
.title::after {
  content: " →";
}

.description {
  display: block;
  position: relative;
  width: 238px;
  font-size: 12px;
  line-height: 22px;
}</style>
